import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { formatApiErrorDetail } from "../../lib/api";
import MetaTags from "../../lib/MetaTags";
import { Lock } from "lucide-react";

const PIN_LENGTH = 4;

export default function AdminPinLogin() {
  const { user, loginPin } = useAuth();
  const [digits, setDigits] = useState(Array(PIN_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/admin" replace />;

  const setDigit = (i, val) => {
    const v = val.replace(/\D/g, "").slice(0, 1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < PIN_LENGTH - 1) {
      document.getElementById(`pin-${i + 1}`)?.focus();
    }
  };

  const onKeyDown = (i) => (e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      document.getElementById(`pin-${i - 1}`)?.focus();
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const pin = digits.join("");
    if (pin.length !== PIN_LENGTH) {
      setError(`Enter your ${PIN_LENGTH}-digit PIN.`);
      return;
    }
    setError("");
    setLoading(true);
    try {
      await loginPin(pin);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Invalid PIN");
      setDigits(Array(PIN_LENGTH).fill(""));
      document.getElementById("pin-0")?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F0] grid place-items-center px-4" data-testid="admin-login-page">
      <MetaTags title="Restricted" />
      <form onSubmit={submit} className="w-full max-w-md bg-white border border-[#121212] p-8 ceg-fade-up">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#121212] text-white grid place-items-center">
            <Lock size={18} />
          </div>
          <div>
            <p className="ceg-overline">Restricted</p>
            <h1 className="font-['Outfit'] font-black tracking-tighter text-3xl">Enter PIN</h1>
          </div>
        </div>
        <p className="text-sm text-[#5C5C5C] mt-3">Chicago Electronics Guide editorial panel.</p>

        {error && (
          <div className="mt-5 p-3 border-l-4 border-[#FF3823] bg-red-50 text-sm" data-testid="login-error">
            {error}
          </div>
        )}

        <div className="mt-6 flex justify-center gap-3" data-testid="pin-inputs">
          {digits.map((d, i) => (
            <input
              key={i}
              id={`pin-${i}`}
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={onKeyDown(i)}
              autoFocus={i === 0}
              className="w-14 h-16 text-center text-2xl font-['Outfit'] font-bold border border-[#121212] outline-none focus:border-[#0055FF] focus:ring-2 focus:ring-[#0055FF]/20"
              data-testid={`pin-digit-${i}`}
            />
          ))}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="ceg-btn-primary w-full mt-6 disabled:opacity-60"
          data-testid="login-submit"
        >
          {loading ? "Verifying..." : "Unlock"}
        </button>
      </form>
    </div>
  );
}
